// Renders the guided lesson: a single scroll of chapters, each one posing a
// problem, letting the learner drive a live engine, then resolving it and
// handing off the problem that motivates the next chapter.

import { CHAPTERS } from "./chapters";
import { MiniDemo } from "./minidemo";
import { IoDemo } from "./iodemo";

const el = (tag: string, cls?: string, text?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};

export function renderLesson(host: HTMLElement, onOpenSandbox: () => void): void {
  host.textContent = "";

  // ---- hero
  const hero = el("header", "lesson-hero");
  hero.appendChild(el("p", "hero-eyebrow", "An interactive lesson"));
  hero.appendChild(el("h1", "hero-title", "How an LSM tree actually works"));
  hero.appendChild(
    el(
      "p",
      "hero-lede",
      "LSM trees are the storage engine behind RocksDB, Cassandra, LevelDB, HBase and " +
        "InfluxDB. This page builds one up from first principles — starting with the " +
        "problem it solves, adding one piece at a time, each because the last piece " +
        "created a new problem.",
    ),
  );
  hero.appendChild(
    el(
      "p",
      "hero-sub",
      "Everything you drive here is a real storage engine running in your browser — " +
        "the same one, shrunk down so you can watch individual keys. No animations, " +
        "no canned sequences.",
    ),
  );
  const heroActions = el("div", "hero-actions");
  const start = el("button", "btn-primary", "Start from the beginning ↓");
  start.onclick = () =>
    document.getElementById("ch-problem")?.scrollIntoView({ behavior: "smooth" });
  const skip = el("button", "btn-ghost", "I know this — open the sandbox");
  skip.onclick = onOpenSandbox;
  heroActions.append(start, skip);
  hero.appendChild(heroActions);
  host.appendChild(hero);

  // ---- progress rail
  const rail = el("nav", "lesson-rail");
  CHAPTERS.forEach((c, i) => {
    const a = el("a", "rail-link");
    a.setAttribute("href", `#ch-${c.id}`);
    a.appendChild(el("span", "rail-num", String(i)));
    a.appendChild(el("span", "rail-label", c.nav));
    a.onclick = (ev) => {
      ev.preventDefault();
      document.getElementById(`ch-${c.id}`)?.scrollIntoView({ behavior: "smooth" });
    };
    rail.appendChild(a);
  });
  host.appendChild(rail);

  // ---- chapters
  const main = el("main", "lesson-main");
  CHAPTERS.forEach((c, i) => {
    const sec = el("section", "chapter");
    sec.id = `ch-${c.id}`;

    const head = el("div", "chapter-head");
    head.appendChild(el("span", "chapter-kicker", c.kicker));
    head.appendChild(el("span", "chapter-num", `${i} / ${CHAPTERS.length - 1}`));
    sec.appendChild(head);
    sec.appendChild(el("h2", "chapter-title", c.title));

    for (const p of c.body) sec.appendChild(el("p", "chapter-para", p));

    if (c.aside) {
      const aside = el("aside", "chapter-aside");
      aside.appendChild(el("h4", undefined, c.aside.title));
      aside.appendChild(el("p", undefined, c.aside.text));
      sec.appendChild(aside);
    }

    if (c.tryIt) {
      const t = el("div", "try-it");
      t.appendChild(el("span", "try-badge", "Try it"));
      t.appendChild(el("span", "try-text", c.tryIt));
      sec.appendChild(t);
    }

    // Chapter 0 gets the labelled cost illustration; every other chapter
    // that declares a demo gets a live engine instance.
    if (c.id === "problem") {
      const holder = el("div", "demo-holder");
      sec.appendChild(holder);
      new IoDemo(holder);
    } else if (c.demo) {
      const holder = el("div", "demo-holder");
      sec.appendChild(holder);
      new MiniDemo(holder, c.demo);
    }

    for (const p of c.after ?? []) sec.appendChild(el("p", "chapter-para", p));

    if (c.cliffhanger) {
      const cliff = el("div", "cliffhanger");
      cliff.appendChild(el("span", "cliff-label", "The new problem"));
      cliff.appendChild(el("p", "cliff-text", c.cliffhanger));
      sec.appendChild(cliff);
    }

    if (c.id === "sandbox") {
      const cta = el("div", "sandbox-cta");
      const b = el("button", "btn-primary btn-large", "Open the sandbox →");
      b.onclick = onOpenSandbox;
      cta.appendChild(b);
      cta.appendChild(
        el(
          "p",
          "cta-note",
          "Full instrument panel: workload generators, live amplification metrics, " +
            "adjustable knobs, and IndexedDB persistence so a page refresh is a real crash test.",
        ),
      );
      sec.appendChild(cta);
    }

    main.appendChild(sec);
  });
  host.appendChild(main);

  const footer = el("footer", "lesson-footer");
  footer.appendChild(
    el(
      "p",
      undefined,
      "Built as a working storage engine first and a visualisation second — " +
        "the panels above are instrumentation over code that genuinely runs.",
    ),
  );
  host.appendChild(footer);

  installRailTracking(rail);
}

/** Highlight the rail entry for whichever chapter is currently on screen. */
function installRailTracking(rail: HTMLElement): void {
  const links = [...rail.querySelectorAll<HTMLElement>(".rail-link")];
  const observer = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const id = e.target.id.replace(/^ch-/, "");
        links.forEach((l) =>
          l.classList.toggle("active", l.getAttribute("href") === `#ch-${id}`),
        );
      }
    },
    { rootMargin: "-45% 0px -45% 0px" },
  );
  for (const c of CHAPTERS) {
    const node = document.getElementById(`ch-${c.id}`);
    if (node) observer.observe(node);
  }
}
